<script setup lang="ts">
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import {useI18n} from 'vue-i18n';
import {useUserStore} from '@/ts/store/user-store';
import {storeToRefs} from 'pinia';
import {GenderDTO} from '@/ts/types/dto/gender-dto';
import type {EducationLevelDTO} from '@/ts/types/dto/sign-in-dto';
import {computed, ref, onMounted} from 'vue';
import UserAvatar from '@/vue/molecules/user-avatar.vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useRegionName} from '@/ts/composables/pure/use-region-name';
import {useLoadingNotification} from '@/ts/composables/pure/use-loading-notification';
import {useQuasar, QForm} from 'quasar';
import GenderSelector from '@/vue/atoms/gender-selector.vue';
import EducationSelector from '@/vue/atoms/education-selector.vue';
import {useRequiredRule, useRequiredNumber, useYearsOfBirth} from '@/ts/composables/pure/use-input-rules';
import {useSignOut} from '@/ts/composables/stateful/use-auth';
import {pagesPath} from '@/ts/router/pages-path';
import DeleteAccountDialog from '@/vue/molecules/delete-account-dialog.vue';

const {t} = useI18n();
const userStore = useUserStore();
const signOut = useSignOut();
const {userData} = storeToRefs(userStore);
const api = useApi();
const $q = useQuasar();

// Form and validation rules
const profileForm = ref<QForm>(null!);
const requiredRule = useRequiredRule();
const requiredNumberRule = useRequiredNumber();
const [minYear, maxYear] = useYearsOfBirth();

// Form fields (using same structure as sign-up form)
const name = ref<string>('');
const gender = ref<{ label: string; value: GenderDTO } | null>(null);
const birthYear = ref<number | null>(null);
const education = ref<EducationLevelDTO | null>(null);

// Initialize form with current user data
const initializeForm = (): void => {
  if (userData.value) {
    name.value = userData.value.name;
    // Find matching gender option (same logic as GenderSelector)
    if (userData.value.gender) {
      const genderOptions = {
        [GenderDTO.PREFER_NOT_TO_SAY]: t('prefer_not_to_say'),
        [GenderDTO.MALE]: t('male'),
        [GenderDTO.FEMALE]: t('female'),
        [GenderDTO.NON_BINARY]: t('non_binary'),
      };
      const options = Object.entries(genderOptions)
        .map(([value, label]) => ({label, value: value as GenderDTO}));
      gender.value = options.find(option => option.value === userData.value!.gender) ?? null;
    }
    birthYear.value = userData.value.birthYear;
    education.value = userData.value.educationLevel;
  }
};

onMounted(initializeForm);

// Save profile changes
const [saveProfile, loading] = useLoadingNotification(
  async() => {
    if (!profileForm.value) {return;}
    
    const isValid = await profileForm.value.validate();
    if (!isValid) {return;}
    
    const updatedData = {
      name: name.value,
      gender: gender.value!.value,
      birthYear: birthYear.value!,
      educationLevel: education.value!,
    };
    
    await api.me.updateMe(updatedData);
    
    // Update local user store
    if (userData.value) {
      userData.value.name = updatedData.name;
      userData.value.gender = updatedData.gender;
      userData.value.birthYear = updatedData.birthYear;
      userData.value.educationLevel = updatedData.educationLevel;
    }
  },
  () => {
    $q.notify(t('profile_updated'));
  },
);

// Check if form has changes
const hasChanges = computed(() => {
  if (!userData.value) {return false;}
  
  return (
    name.value !== userData.value.name ||
    gender.value?.value !== userData.value.gender ||
    birthYear.value !== userData.value.birthYear ||
    education.value !== userData.value.educationLevel
  );
});

const displayEmail = computed(() => userData.value?.email ?? '');
const displayRegion = useRegionName();

function requestAccountDeletion(): void {
  $q.dialog({
    component: DeleteAccountDialog,
  }).onOk(() => {
    $q.notify(t('delete_account_success'));
    void signOut(pagesPath.public.signUp, {skipBeLogout: true});
  });
}
</script>

<template>
  <div class="account-tab-page">
    <h3 class="info-title">
      {{ $t('your_information') }}
    </h3>

    <div class="info-container">
      <!-- Profile avatar and basic info -->
      <div class="profile-header">
        <div class="avatar-container">
          <user-avatar/>
        </div>
      </div>

      <!-- User information form (editable) -->
      <q-form ref="profileForm" class="user-info-form" @submit="saveProfile">
        <div class="form-row">
          <q-input
            v-model="name"
            :label="$t('name')"
            :rules="[requiredRule]"
            autocomplete="name"
            class="form-field"
          />
          <q-input
            :model-value="displayEmail"
            :label="$t('email')"
            readonly
            class="form-field"
          />
        </div>
      
        <div class="form-row">
          <q-input
            v-model.number="birthYear"
            type="number"
            :label="$t('year_of_birth')"
            :rules="[requiredNumberRule, minYear, maxYear]"
            autocomplete="bday-year"
            class="form-field"
          />
          <gender-selector
            v-model="gender"
            class="form-field"
          />
        </div>
      
        <div class="form-row">
          <q-input
            :model-value="displayRegion"
            :label="$t('region')"
            readonly
            class="form-field"
          />
          <education-selector
            v-model="education"
            class="form-field"
          />
        </div>
      
        <div class="form-actions">
          <balm-btn
            :label="$t('save_changes')"
            type="submit"
            theme="grey"
            paddings="p2-5"
            :loading="loading"
            :disable="!hasChanges"
            class="save-btn"
          />
        </div>
      </q-form>
    </div>

    <!-- Delete Account Section -->
    <div class="delete-account-container">
      <div class="delete-text-content">
        <h4 class="delete-title">
          {{ $t('delete_account') }}
        </h4>
        <p class="delete-description">
          {{ $t('delete_account_description') }}
        </p>
      </div>
      <balm-btn
        theme="red"
        paddings="p2-5"
        :label="$t('delete_account')"
        icon="trash.svg"
        icon-position="left"
        class="delete-btn"
        @click="requestAccountDeletion"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.account-tab-page {
  @include q-card-white;
  padding: var(--Spacing-spacing-5);
}

.info-title {
  @include font-display-3;
  color: var(--Neutral-UI-neutral-ui-100);
  margin-bottom: var(--Spacing-spacing-5);
}

.info-container {
  background: var(--Neutral-UI-neutral-ui-10);
  border-radius: var(--card-border-radius);
  padding: var(--Spacing-spacing-6);
}

.profile-header {
  margin-bottom: var(--Spacing-spacing-6);

  .avatar-container {
    display: flex;
    align-items: center;

    // Scale up the user avatar for profile page
    :deep(.user-avatar) {
      .img-holder {
        width: 80px;
        height: 80px;
      }

      // Hide username in profile header since it's shown in the form below
      .username {
        display: none;
      }
    }
  }
}

.user-info-form {
  .form-row {
    display: flex;
    flex-direction: column;
    gap: var(--Spacing-spacing-4);
    margin-bottom: var(--Spacing-spacing-6);
    max-width: 1000px;

    @include screen-md {
      flex-direction: row;
      gap: var(--Spacing-spacing-6);
    }

    .form-field {
      flex: 1;
    }
  }

  .form-actions {
    margin-top: var(--Spacing-spacing-6);
    display: flex;
    justify-content: flex-end;

    .save-btn {
      min-width: 140px;
    }
  }
}

.delete-account-container {
  padding: var(--Spacing-spacing-6) var(--Spacing-spacing-9) var(--Spacing-spacing-7) var(--Spacing-spacing-7);
  margin-top: var(--Spacing-spacing-7);

  .delete-text-content {
    .delete-title {
      @include font-body-bold;
      color: var(--Neutral-UI-neutral-ui-100);
      margin: 0 0 var(--Spacing-spacing-3) 0;
    }

    .delete-description {
      @include font-body;
      color: var(--Neutral-UI-neutral-ui-80);
      margin: 0;
      max-width: 600px;
      line-height: 1.6;
    }
  }

  .delete-btn {
    margin-top: var(--Spacing-spacing-6);
  }
}
</style>
